/**
 * A chunk of the application that never arrived.
 *
 * Every build names its files by content hash and a publish replaces the set,
 * so a browser holding a superseded document asks for a hash the current
 * snapshot no longer serves. The import rejects, and every engine words it
 * differently — this module holds the one list of those wordings, the keys the
 * failure is remembered under, and the shape of what is remembered.
 *
 * The inline boot watchdog runs before any module is loaded and so cannot
 * import this file; `hydrationWatchdog.ts` writes these same values into the
 * script text instead, which is why they are literals rather than behaviour.
 */

/**
 * Substrings, one per engine, of the message a failed module fetch produces.
 * Chrome and Safari word the same failure differently, Firefox again, and
 * Vite's preload helper adds its own.
 */
export const CHUNK_FAILURE_MESSAGES = [
  "dynamically imported module",
  "Importing a module script failed",
  "error loading dynamically imported module",
  "Loading chunk",
] as const;

/** True when `message` is a browser reporting a module that would not load. */
export function isChunkLoadFailure(message: string): boolean {
  return CHUNK_FAILURE_MESSAGES.some((phrase) => message.includes(phrase));
}

/**
 * What the application's own parsing says when it fails, in every engine:
 * `JSON.parse` names JSON, and a pattern names a regular expression.
 */
export const OWN_PARSE = /JSON|regular expression|regexp/i;

/**
 * True when `error` is the browser refusing to parse a fetched module at all.
 * A stale chunk request that lands on a JSON or HTML body instead of the
 * expected script throws this way — "Unexpected token '{'", "unexpected end
 * of input", worded differently again per engine — rather than one of the
 * fetch-failure wordings above. A parse failure is told apart by error type,
 * not message text: the wording varies too much to list, and no build this
 * application ships ever fails to parse. What its code parses at run time
 * can, and says so — data that is not JSON, a pattern that is not a regular
 * expression — and that is a render bug a reload does not fix.
 */
export function isChunkParseFailure(error: unknown): boolean {
  return error instanceof SyntaxError && !OWN_PARSE.test(error.message);
}

/**
 * True when a thrown value is a chunk that did not arrive or did not parse —
 * the one test every place that tells a chunk failure apart shares, so the
 * screen a visitor sees and the report made about it never disagree. React
 * passes on whatever was thrown, and a value that is not an `Error` is not one.
 */
export function isChunkFailure(error: unknown): error is Error {
  if (!(error instanceof Error)) return false;
  return isChunkLoadFailure(error.message) || isChunkParseFailure(error);
}

/**
 * The path this application's own chunks are served under. Every script the
 * build emits is a hashed file in this directory, so a same-origin `<script>`
 * that fails outside it is not one of ours — an extension or an in-app browser
 * injected it against this origin — and its failure is no reason to reload the
 * page. The boot watchdog holds to it when the failing element is all it has to
 * go on. Vite's default assets directory; a build that moves it moves this too.
 */
export const APP_CHUNK_PATH = "/assets/";

/**
 * What a report says about a chunk whose message named no URL. The wording is
 * the watchdog's, which has always had to report from an `error` event that
 * carried nothing else.
 */
export const UNNAMED_CHUNK = "unknown";

/**
 * The module URL a failure message names, or `UNNAMED_CHUNK`.
 *
 * Every engine words the failure differently but each one that knows the URL
 * puts it in the message — the wordings in `CHUNK_FAILURE_MESSAGES` differ
 * around it, not in it. Firefox names a bare specifier instead on some
 * failures, and a report that invented a URL for that would be worse than one
 * that says it has none.
 */
export function chunkUrlFromMessage(message: string): string {
  return /https?:\/\/[^\s'"<>)]+/.exec(message)?.[0] ?? UNNAMED_CHUNK;
}

interface BrowserError extends Error {
  sourceURL?: unknown;
  fileName?: unknown;
  filename?: unknown;
}

type BrowserErrorField = "sourceURL" | "fileName" | "filename";

function hasBrowserErrorField(
  error: Error,
  field: BrowserErrorField,
): error is BrowserError {
  return field in error;
}

function chunkUrlFromErrorText(value: unknown): string {
  if (typeof value !== "string") return UNNAMED_CHUNK;
  return chunkUrlFromMessage(value).replace(/:\d+:\d+$/, "");
}

/**
 * The module URL a thrown error names, or `UNNAMED_CHUNK`.
 *
 * A parse failure's message never carries the URL — "Unexpected token '{'"
 * names nothing — so a runtime chunk-parse report built from the message alone
 * is always `UNNAMED_CHUNK`. Browsers may put it in a source field instead;
 * the stack trace is the final fallback.
 */
export function chunkUrlFromError(error: Error): string {
  const fromMessage = chunkUrlFromErrorText(error.message);
  if (fromMessage !== UNNAMED_CHUNK) return fromMessage;

  for (const source of ["sourceURL", "fileName", "filename"] as const) {
    if (!hasBrowserErrorField(error, source)) continue;
    const fromSource = chunkUrlFromErrorText(error[source]);
    if (fromSource !== UNNAMED_CHUNK) return fromSource;
  }

  return chunkUrlFromErrorText(error.stack);
}

/** Where the failure happened, which decides how it can be recovered. */
export type ChunkFailurePhase = "boot" | "runtime";

/** What is reported about a chunk that would not load. */
export interface ChunkFailureReport {
  /** The module URL, or the specifier when the browser named no URL. */
  chunk: string;
  /** The page the visitor was on. */
  path: string;
  phase: ChunkFailurePhase;
  /** How many fetches were made in total, the first one included. */
  attempts: number;
  /** Whether a later attempt delivered the chunk after all. */
  recovered: boolean;
}

/**
 * Session storage key for the one-shot boot reload. Its presence means the
 * page has already spent its reload for this browsing session.
 */
export const HYDRATION_RETRY_KEY = "syncani.hydration-retry";

/**
 * Session storage key for a boot failure waiting to be reported. Nothing can
 * be sent while the bundle is the thing that is missing, so the watchdog
 * leaves the report here and the app sends it once it is running.
 */
export const CHUNK_FAILURE_KEY = "syncani.chunk-failure";

/** One automatic reload for a chunk lost after the application is running. */
export const CHUNK_RELOAD_KEY = "syncani.chunk-reload";

/** The half of `Storage` this module uses; a test supplies its own. */
export type ChunkFailureStore = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
>;

export function parseChunkFailureReport(
  raw: string,
): ChunkFailureReport | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return null;
    const record: Record<string, unknown> = { ...parsed };
    const { chunk, path, phase, attempts, recovered } = record;
    if (typeof chunk !== "string" || typeof path !== "string") return null;
    if (phase !== "boot" && phase !== "runtime") return null;
    return {
      chunk,
      path,
      phase,
      attempts: typeof attempts === "number" ? attempts : 1,
      recovered: recovered === true,
    };
  } catch {
    return null;
  }
}

/**
 * Reads the boot failure left for the app to report and forgets it, so one
 * failure is reported once. Returns null when there is nothing to report or
 * what is there cannot be read — a report is never worth a thrown error.
 */
export function takeRecordedChunkFailure(
  store: ChunkFailureStore,
): ChunkFailureReport | null {
  try {
    const raw = store.getItem(CHUNK_FAILURE_KEY);
    store.removeItem(CHUNK_FAILURE_KEY);
    return raw === null ? null : parseChunkFailureReport(raw);
  } catch {
    /* private mode, or something else wrote the key */
    return null;
  }
}
